'use client';
import type { MinimalFeaturesQueryResult } from '@app/sanity.types';
import { Flex, Grid, Menu, MenuProps } from 'antd';
import { SubMenuType } from 'antd/es/menu/interface';
import cn from 'classnames';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC, useMemo, useState } from 'react';
import styles from 'sailor-ui/styles/Header.module.scss';
import { BlogsMenu } from './BlogMenu';
import { BoatsMenu } from './BoatsMenu';
import { FeaturesMenu } from './FeaturesMenu';
import { FeaturesMenuItems } from './FeaturesMenu/FeaturesMenu.component';
import { MainDrawer } from './MainDrawer';
import { MoreMenu } from './MoreMenu';
import { useMoreMenuItems } from './MoreMenu/useMoreMenuItems';
import { menuKeys } from './menuKeys';

interface LeftMenuProps {
  featuresItems?: MinimalFeaturesQueryResult;
}

const LeftMenu: FC<LeftMenuProps> = ({ featuresItems = [] }) => {
  const pathname = usePathname();
  const { xl: responsive } = Grid.useBreakpoint();
  const moreMenuItems = useMoreMenuItems();
  const featuresMenuItems = FeaturesMenuItems(featuresItems);

  const [moreDrawer, setMoreDrawer] = useState<boolean>(false);
  const [featureDrawer, setFeatureDrawer] = useState<boolean>(false);
  const [boatDrawer, setBoatDrawer] = useState<boolean>(false);
  const [blogDrawer, setBlogDrawer] = useState<boolean>(false);

  const selectedKeys = useMemo(() => {
    const activeMenuKey = menuKeys.find((item) => item.validPaths.some((path) => path === pathname));

    if (!activeMenuKey) {
      const itemStartsWith =
        menuKeys.find((item) => {
          if (item?.startsWith) {
            return pathname.startsWith(item?.startsWith);
          }

          return undefined;
        })?.key || '/no-match';

      return itemStartsWith;
    }

    return activeMenuKey?.key || '/no-match';
  }, [pathname]);

  const menuItems = useMemo((): MenuProps['items'] => {
    const mouseHandler: SubMenuType['onMouseEnter'] = (e) => {
      e.domEvent.preventDefault();
    };

    const CANCEL_DEFAULT_MOUSE_EVENTS: Pick<SubMenuType, 'onMouseEnter' | 'onMouseLeave'> = {
      onMouseEnter: mouseHandler,
      onMouseLeave: mouseHandler,
    };

    if (responsive) {
      return [
        {
          key: 'boats',
          label: <BoatsMenu key="boats" />,
          ...CANCEL_DEFAULT_MOUSE_EVENTS,
        },
        // {
        //   key: 'discover',
        //   label: (
        //     <Link href="/discover">
        //       <a className="ant-menu-item--badge">
        //         {f('discover.title')}
        //         <span className="menuItemBadge">{f('discover.badge')}</span>
        //       </a>
        //     </Link>
        //   ),
        // },
        {
          key: 'maker',
          label: (
            <Link href="/maker" className="ant-menu-item--badge">
              Makers
            </Link>
          ),
        },
        {
          key: 'blogs',
          label: <BlogsMenu key="blogs-desktop" />,
          ...CANCEL_DEFAULT_MOUSE_EVENTS,
        },

        {
          key: 'features',
          label: (
            <Link href="/features">
              <Flex align="center" gap={8}>
                <span>Features</span>
                <ChevronDown size={16} />
              </Flex>
            </Link>
          ),
          children: featuresMenuItems,
          popupClassName: styles.subMenuDropdown,
        },
        {
          key: 'more',
          label: (
            <Flex align="center" gap={8}>
              <span>More</span>
              <ChevronDown size={16} />
            </Flex>
          ),
          children: moreMenuItems,
          popupClassName: styles.subMenuDropdown,
        },
      ];
    }

    return [
      {
        key: 'boats',
        style: {
          display: 'flex',
          justifyContent: 'space-between',
        },
        onClick: () => {
          setBoatDrawer(true);
        },
        label: (
          <>
            Boats <ChevronRight size={16} />
          </>
        ),
      },
      // {
      //   key: 'discover',
      //   label: (
      //     <Link href="/discover">
      //       <a className="ant-menu-item--badge">
      //         {f('discover.title')}
      //         <span className="menuItemBadge">{f('discover.badge')}</span>
      //       </a>
      //     </Link>
      //   ),
      // },
      {
        key: 'maker',
        label: (
          <Link href="/maker" className="ant-menu-item--badge">
            Maker
          </Link>
        ),
      },
      {
        key: 'blogs',
        label: (
          <>
            Blogs
            <ChevronRight size={16} />
          </>
        ),
        onClick: () => {
          setBlogDrawer(true);
        },
      },
      {
        key: 'more-menu-trigger',
        onClick: () => {
          setMoreDrawer(true);
        },
        label: (
          <>
            More
            <ChevronRight size={16} />
          </>
        ),
      },
      {
        key: 'feature-menu-trigger',
        onClick: () => {
          setFeatureDrawer(true);
        },
        label: (
          <>
            Features <ChevronRight size={16} />
          </>
        ),
      },
    ];
  }, [responsive, featuresMenuItems, moreMenuItems]);

  return (
    <>
      <Menu
        mode={responsive ? 'horizontal' : 'inline'}
        selectedKeys={[selectedKeys]}
        className={cn({ [styles.leftMenuDesktop]: responsive })}
        items={menuItems}
      />

      <MainDrawer
        visible={moreDrawer}
        onClose={() => {
          setMoreDrawer(false);
        }}
        className={styles.normalDrawer}
        closable={false}
      >
        <MoreMenu />
      </MainDrawer>

      <MainDrawer
        visible={featureDrawer}
        onClose={() => {
          setFeatureDrawer(false);
        }}
        className={styles.normalDrawer}
        closable={false}
      >
        <FeaturesMenu items={featuresItems} />
      </MainDrawer>

      <MainDrawer
        visible={boatDrawer}
        onClose={() => {
          setBoatDrawer(false);
        }}
        closable={false}
        className={styles.normalDrawer}
      >
        <BoatsMenu />
      </MainDrawer>

      <MainDrawer
        visible={blogDrawer}
        onClose={() => {
          setBlogDrawer(false);
        }}
        closable={false}
        className={styles.normalDrawer}
      >
        <Menu
          mode="inline"
          items={[
            {
              key: 'blog',
              label: (
                <Link
                  href="/blog"
                  onClick={() => {
                    setBlogDrawer(false);
                  }}
                >
                  Blog
                </Link>
              ),
            },
            {
              key: 'news',
              label: (
                <Link
                  href="/blog/category/news"
                  onClick={() => {
                    setBlogDrawer(false);
                  }}
                >
                  News
                </Link>
              ),
            },
          ]}
        />
      </MainDrawer>
    </>
  );
};

export default LeftMenu;
